import React, { useEffect, useMemo, useState } from "react";
import { Flame } from "lucide-react";

const pad = (n) => String(n).padStart(2, "0");

const useTimeLeft = (isoEnd) => {
  const end = useMemo(() => (isoEnd ? new Date(isoEnd).getTime() : 0), [isoEnd]);
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!end) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [end]);

  if (!end) return null;
  const diff = Math.max(0, end - now);
  const days = Math.floor(diff / 86_400_000);
  const hours = Math.floor((diff % 86_400_000) / 3_600_000);
  const mins = Math.floor((diff % 3_600_000) / 60_000);
  const secs = Math.floor((diff % 60_000) / 1000);
  return { diff, days, hours, mins, secs };
};

const Unit = ({ value, label }) => (
  <div className="flex-1 min-w-0 text-center">
    <div className="font-mono text-2xl md:text-3xl text-fin-green tabular-nums">
      {pad(value)}
    </div>
    <div className="text-[9px] tracking-overline text-white/40 mt-0.5">{label}</div>
  </div>
);

/**
 * Launch-offer countdown banner.
 *  - variant="card" (default): full block for the sticky purchase card.
 *  - variant="pill": small pill for course grid cards.
 */
const CountdownTimer = ({ endsAt, variant = "card", label = "Launch offer ends in", testId = "offer-countdown" }) => {
  const t = useTimeLeft(endsAt);
  if (!t || t.diff <= 0) return null;

  if (variant === "pill") {
    const label =
      t.days > 0
        ? `${t.days}d ${pad(t.hours)}h left`
        : `${pad(t.hours)}:${pad(t.mins)}:${pad(t.secs)} left`;
    return (
      <div className="inline-flex items-center gap-1 bg-fin-green/15 text-fin-green text-[10px] font-bold tracking-overline px-2 py-1 rounded-sm">
        <Flame className="h-2.5 w-2.5" />
        {label}
      </div>
    );
  }

  return (
    <div
      data-testid={testId}
      className="rounded-md border border-fin-green/25 bg-fin-green/[0.06] p-3"
    >
      <div className="flex items-center gap-2 text-fin-green text-[10px] tracking-overline mb-2">
        <Flame className="h-3 w-3" />
        <span>{label}</span>
      </div>
      <div className="flex items-stretch gap-2">
        <Unit value={t.days} label="Days" />
        <div className="text-white/20 text-2xl md:text-3xl font-mono">:</div>
        <Unit value={t.hours} label="Hours" />
        <div className="text-white/20 text-2xl md:text-3xl font-mono">:</div>
        <Unit value={t.mins} label="Mins" />
        <div className="text-white/20 text-2xl md:text-3xl font-mono">:</div>
        <Unit value={t.secs} label="Secs" />
      </div>
    </div>
  );
};

export default CountdownTimer;
